import { ViewColumnsIcon } from "@heroicons/react/24/outline";
import { GetActivityFeedQuery } from "generated/graphql";
import React from "react";
import { EntityActivitySummary } from "./details/entity-activity-summary";
import { ActivityEntityType, EntityActivityEntryProps } from "./types";

const stageChange = (
    activity: GetActivityFeedQuery["getActivityFeed"]["activities"][0],
) => {
    return (
        activity.activity.metadata?.events?.find(e => e.pipelineStageChange)
            ?.pipelineStageChange ?? null
    );
};

const entityNoun = (entityType: ActivityEntityType): string => {
    if (entityType === "Client") {
        return "this client";
    }
    if (entityType === "Community") {
        return "this community";
    }
    if (entityType === "ReferenceContact") {
        return "this contact";
    }
    if (entityType === "ReferenceBusiness") {
        return "this organization";
    }
    if (entityType === "HomeCareLead") {
        return "this home care client";
    }
    const exhaustiveCheck: never = entityType;
    throw new Error(`Unhandled entity type: ${exhaustiveCheck}`);
};

type PipelineCardActivityProps = EntityActivityEntryProps & {
    entityType: ActivityEntityType;
};

const PipelineCardActivity: React.FC<
    PipelineCardActivityProps & {
        render: (change: PipelineStageChangeShape, noun: string) => React.ReactNode;
    }
> = props => {
    const change = stageChange(props.activity);
    if (!change) {
        return null;
    }
    return (
        <div className="relative flex items-start space-x-3">
            <div className="relative px-1">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white">
                    <ViewColumnsIcon
                        className="h-5 w-5 text-gray-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <div className="min-w-0 flex-1 group pt-1">
                <EntityActivitySummary {...props}>
                    {props.render(change, entityNoun(props.entityType))}
                </EntityActivitySummary>
            </div>
        </div>
    );
};

type PipelineStageChangeShape = NonNullable<ReturnType<typeof stageChange>>;

export const EntityActivityPipelineCardAdded: React.FC<
    PipelineCardActivityProps
> = props => (
    <PipelineCardActivity
        {...props}
        render={(change, noun) => (
            <>
                added {noun} to the <strong>{change.pipelineName}</strong> pipeline in{" "}
                <strong>{change.toStageName}</strong>.
            </>
        )}
    />
);

export const EntityActivityPipelineCardMoved: React.FC<
    PipelineCardActivityProps
> = props => (
    <PipelineCardActivity
        {...props}
        render={(change, noun) => (
            <>
                moved {noun} from <strong>{change.fromStageName}</strong> to{" "}
                <strong>{change.toStageName}</strong> in the{" "}
                <strong>{change.pipelineName}</strong> pipeline.
            </>
        )}
    />
);

export const EntityActivityPipelineCardRemoved: React.FC<
    PipelineCardActivityProps
> = props => (
    <PipelineCardActivity
        {...props}
        render={(change, noun) => (
            <>
                removed {noun} from the <strong>{change.pipelineName}</strong> pipeline.
            </>
        )}
    />
);
